import React from "react"

import { Popover, PopoverAnchor, PopoverContent, PopoverItem } from "@coval/ui"
import { FileText, MessageSquare, ScrollText } from "lucide-react"

import { TestSetType } from "@/types/TestSetInput"

import type { EditableTestCase } from "../../../model"
import { LearnedTestCaseBadge } from "../../auto-add/TestSetAutoAddSection"
import { useGridCell } from "./useGridCell"

interface TestCaseTypeCellProps {
  testCase: EditableTestCase
  onUpdate: (updates: Partial<EditableTestCase>) => void
  width: string
}

const TYPE_CONFIG = {
  SCENARIO: {
    icon: FileText,
    label: "Scenario",
    description: "High-level prompt. The simulated user improvises freely.",
  },
  TRANSCRIPT: {
    icon: MessageSquare,
    label: "Transcript",
    description:
      "A reference conversation. The simulated user adapts as needed.",
  },
  SCRIPT: {
    icon: ScrollText,
    label: "Script",
    description: "Exact turns. The simulated user follows them precisely.",
  },
}

type SelectableTestSetType = keyof typeof TYPE_CONFIG

function isSelectableTestSetType(
  value: string | null | undefined,
): value is SelectableTestSetType {
  return value != null && Object.hasOwn(TYPE_CONFIG, value)
}

export const TestCaseTypeCell: React.FC<TestCaseTypeCellProps> = ({
  testCase,
  onUpdate,
  width,
}) => {
  const {
    isEditing: isOpen,
    setIsEditing: setIsOpen,
    cellProps,
    tdRef,
  } = useGridCell({
    ariaLabel: "Change test case type",
    mode: "popover",
  })

  const currentType = isSelectableTestSetType(testCase.input_type)
    ? testCase.input_type
    : null
  const CurrentIcon = currentType ? TYPE_CONFIG[currentType].icon : FileText
  const currentLabel = currentType
    ? TYPE_CONFIG[currentType].label
    : TYPE_CONFIG.SCENARIO.label

  const handleTypeChange = (newType: TestSetType, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
    }
    onUpdate({ input_type: newType })
    setIsOpen(false)
    tdRef.current?.focus()
  }

  return (
    <td
      {...cellProps}
      className="p-0 outline-none"
      style={{ width, padding: 0, verticalAlign: "top" }}
    >
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverAnchor asChild>
          <div className="py-3 px-2">
            <span className="inline-flex items-center gap-1.5 text-coval-text-muted">
              <CurrentIcon className="w-4 h-4 shrink-0" />
              <span className="text-sm truncate">{currentLabel}</span>
            </span>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="p-1 min-w-[260px] max-w-[300px]"
          onInteractOutside={(e) => {
            // Clicking the cell toggles via the cell handler; don't double-dismiss.
            if (tdRef.current?.contains(e.target as Node)) {
              e.preventDefault()
            }
          }}
          onCloseAutoFocus={(e) => {
            // Keep keyboard flow on the cell rather than the document body.
            e.preventDefault()
            tdRef.current?.focus()
          }}
        >
          <div className="flex flex-col gap-0.5">
            {(
              [
                TestSetType.SCENARIO,
                TestSetType.TRANSCRIPT,
                TestSetType.SCRIPT,
              ] as const
            ).map((type) => {
              const { icon: Icon, label, description } = TYPE_CONFIG[type]
              return (
                <PopoverItem
                  key={type}
                  aria-label={`Select ${label} test case type`}
                  onClick={(e) => handleTypeChange(type, e)}
                  checked={currentType === type}
                  className="items-start gap-3 px-3 py-2 text-left"
                >
                  <Icon className="w-4 h-4 text-coval-text-muted shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-coval-text">{label}</div>
                    <div className="text-xs text-coval-text-muted">
                      {description}
                    </div>
                  </div>
                </PopoverItem>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
      <LearnedTestCaseBadge testCaseId={testCase.id} />
    </td>
  )
}
